import {
  SHOW_DELAY_MS,
  SOFT_HIDE_MS,
  TIP_ID,
  WARM_WINDOW_MS,
} from './constants';
import {
  addDescribedBy,
  findTipTarget,
  isHelpTip,
  removeDescribedBy,
  restoreTitle,
  stashTitle,
  tipContentFor,
} from './tipTargets';

// The tooltip's behaviour, separate from how it looks (TooltipLayer draws
// it). Listens on the document in the capture phase:
//   • hover shows a tip after SHOW_DELAY_MS, or at once when another tip hid
//     less than WARM_WINDOW_MS ago; touch hovers are ignored
//   • keyboard focus (:focus-visible) shows the focused element's tip
//   • Esc, a click elsewhere, scrolling, resizing and dragging hide it
//   • pinned tips ("?" buttons, or a tap on a data-tip-pin element on a
//     touch screen) stay until tapped again or something else is clicked
// Help tips (with a title) follow the Help tips switch unless pinned or
// marked data-tip-pin; labels always show. Nothing shows while the tour
// runs.
//
// onChange({ el, content, pinned }) when a tip shows, onChange(null) when
// it hides.

const defaultKeyboardFocus = (el) => {
  try {
    return el.matches(':focus-visible');
  } catch (e) {
    return false;
  }
};

export const createTipController = ({
  doc = document,
  win = window,
  getRegistry,
  isEnabled = () => true,
  isSuppressed = () => false,
  isActive = () => true,
  isKeyboardFocus = defaultKeyboardFocus,
  onChange = () => {},
  now = () => Date.now(),
}) => {
  let cur = null;
  let pinned = false;
  let pending = null;
  let showTimer = null;
  let hideTimer = null;
  let lastHide = -Infinity;

  const release = (el) => {
    removeDescribedBy(el, TIP_ID);
    restoreTitle(el);
  };

  const cancelPending = () => {
    clearTimeout(showTimer);
    showTimer = null;
    if (pending && pending !== cur) restoreTitle(pending);
    pending = null;
  };

  const hide = () => {
    cancelPending();
    clearTimeout(hideTimer);
    hideTimer = null;
    const was = cur;
    if (was) release(was);
    cur = null;
    pinned = false;
    if (was) {
      lastHide = now();
      onChange(null);
    }
  };

  const show = (el, pin = false) => {
    if (isSuppressed()) return false;
    const content = tipContentFor(el, getRegistry());
    if (!content || (!content.b && !content.t)) return false;
    const always = pin || el.hasAttribute('data-tip-pin');
    if (isHelpTip(content) && !isEnabled() && !always) return false;
    if (cur && cur !== el) release(cur);
    if (el.hasAttribute('title')) stashTitle(el);
    cur = el;
    pinned = !!pin;
    addDescribedBy(el, TIP_ID);
    onChange({ el, content, pinned });
    return true;
  };

  const softHide = () => {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, SOFT_HIDE_MS);
  };

  const toggle = (el) => {
    if (cur === el && pinned) {
      hide();
      return;
    }
    hide();
    show(el, true);
  };

  const onPointerOver = (e) => {
    if (!isActive() || e.pointerType === 'touch' || e.buttons || pinned) return;
    const el = findTipTarget(e.target, getRegistry());
    if (el === cur && el) {
      clearTimeout(hideTimer);
      return;
    }
    if (el && el === pending) return;
    cancelPending();
    if (!el) {
      if (cur) softHide();
      return;
    }
    if (cur) hide();
    // Take the native title off now so the browser's tooltip never shows.
    if (el.hasAttribute('title')) stashTitle(el);
    pending = el;
    const warm = now() - lastHide < WARM_WINDOW_MS;
    showTimer = setTimeout(
      () => {
        pending = null;
        if (!show(el)) restoreTitle(el);
      },
      warm ? 0 : SHOW_DELAY_MS
    );
  };

  const onPointerOut = (e) => {
    if (!isActive() || pinned || e.relatedTarget) return;
    // The pointer left the window.
    cancelPending();
    if (cur) softHide();
  };

  const onFocusIn = (e) => {
    if (!isActive() || pinned) return;
    const target = e.target;
    if (!target || target.nodeType !== 1 || !isKeyboardFocus(target)) return;
    const el = findTipTarget(target, getRegistry());
    if (el !== target) return;
    cancelPending();
    show(el);
  };

  const onFocusOut = (e) => {
    if (!isActive() || pinned || !cur || cur !== e.target) return;
    hide();
  };

  const onPointerDown = (e) => {
    if (!isActive()) return;
    if (e.pointerType === 'touch') {
      const el = findTipTarget(e.target, getRegistry());
      if (
        el &&
        el.hasAttribute('data-tip-pin') &&
        !el.hasAttribute('data-tip-info')
      ) {
        toggle(el);
        return;
      }
    }
    if (!cur) return;
    if (pinned && cur.contains(e.target)) return;
    hide();
  };

  const onKeyDown = (e) => {
    if (isActive() && e.key === 'Escape' && cur) hide();
  };

  const onScroll = () => {
    if (isActive() && cur && !pinned) hide();
  };

  const onHideEvent = () => {
    if (isActive() && cur) hide();
  };

  // Re-check the shown tip: hide it when its element left the page.
  const check = () => {
    if (cur && !cur.isConnected) hide();
    return cur;
  };

  const install = () => {
    doc.addEventListener('pointerover', onPointerOver, true);
    doc.addEventListener('pointerout', onPointerOut, true);
    doc.addEventListener('focusin', onFocusIn, true);
    doc.addEventListener('focusout', onFocusOut, true);
    doc.addEventListener('pointerdown', onPointerDown, true);
    doc.addEventListener('keydown', onKeyDown, true);
    doc.addEventListener('dragstart', onHideEvent, true);
    win.addEventListener('resize', onHideEvent);
    win.addEventListener('scroll', onScroll, true);
    return () => {
      doc.removeEventListener('pointerover', onPointerOver, true);
      doc.removeEventListener('pointerout', onPointerOut, true);
      doc.removeEventListener('focusin', onFocusIn, true);
      doc.removeEventListener('focusout', onFocusOut, true);
      doc.removeEventListener('pointerdown', onPointerDown, true);
      doc.removeEventListener('keydown', onKeyDown, true);
      doc.removeEventListener('dragstart', onHideEvent, true);
      win.removeEventListener('resize', onHideEvent);
      win.removeEventListener('scroll', onScroll, true);
      hide();
    };
  };

  return {
    install,
    show,
    hide,
    toggle,
    check,
    current: () => cur,
    isPinned: () => pinned,
  };
};
